import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getListClosuresQueryKey,
  useCloseFacility,
  useListClosures,
  usePreviewClosure,
  useReopenFacility,
} from '../../../api/generated/endpoints/admin/admin'
import { getGetAvailabilityQueryKey } from '../../../api/generated/endpoints/availability/availability'
import type { ClosurePreview, ClosureRead } from '../../../api/generated/model'
import { apiMessage } from '../../../lib/api-error'
import {
  addMonths,
  dayCount,
  inRange,
  isSameMonth,
  monthGrid,
  monthLabel,
  nextSelection,
} from '../../../lib/month'
import { formatDayLong, formatShortInstant, todayAtFacility } from '../../../lib/time'

/**
 * Closing the facility for a stretch of days.
 *
 * Spec §9 asks for "one switch". A switch was not enough: the real cases are a
 * week of maintenance or the fortnight everybody is at the beach, so this is a
 * calendar you drag a range across.
 *
 * THE CONFIRMATION STEP IS THE POINT OF THE SCREEN. Closing cancels other
 * people's bookings and emails them, and an email cannot be recalled. So
 * nothing happens until the coach has seen a list of exactly who he is about
 * to affect, by name. The button before that says "Ver a quiénes afecta", not
 * "Close".
 */
export const Route = createFileRoute('/_authed/_admin/admin/closures')({
  component: ClosuresPage,
})

const REASONS = [
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'holiday', label: 'Feriado' },
  { value: 'weather', label: 'Clima' },
  { value: 'private_event', label: 'Evento privado' },
  { value: 'other', label: 'Otro motivo' },
] as const

function ClosuresPage() {
  const today = todayAtFacility()
  const [anchor, setAnchor] = useState(today.slice(0, 7))
  const [from, setFrom] = useState<string | null>(null)
  const [to, setTo] = useState<string | null>(null)
  const [reason, setReason] = useState<string>('maintenance')
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)

  const queryClient = useQueryClient()
  const closures = useListClosures()

  // Only fetched once the coach asks. A preview firing on every calendar tap
  // would be a request per click for information nobody has asked for yet.
  const preview = usePreviewClosure(
    { starts_on: from ?? today, ends_on: to ?? from ?? today },
    { query: { enabled: confirming && Boolean(from) } },
  )

  const reset = () => {
    setFrom(null)
    setTo(null)
    setNote('')
    setConfirming(false)
  }

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListClosuresQueryKey() }),
      // Every day's availability may have changed.
      queryClient.invalidateQueries({ queryKey: getGetAvailabilityQueryKey() }),
    ])

  const close = useCloseFacility({
    mutation: {
      retry: false, // never repeat something that cancels bookings
      onSuccess: async () => {
        await refresh()
        reset()
      },
    },
  })

  const alreadyClosed = new Set<string>()
  for (const closure of closures.data?.closures ?? []) {
    for (
      let day = closure.starts_on;
      day <= closure.ends_on;
      day = new Date(Date.parse(`${day}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10)
    ) {
      alreadyClosed.add(day)
    }
  }

  const weeks = monthGrid(`${anchor}-01`)
  const span = from ? dayCount(from, to ?? from) : 0

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold">Cerrar el local</h1>
        <p className="mt-2 max-w-2xl text-slate-700">
          Elegir el primer día y después el último. Todas las reservas de esos días se cancelan, se
          devuelve el importe completo y se avisa por correo.
        </p>
      </div>

      <section className="rounded-lg border-2 border-slate-300 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setAnchor(addMonths(anchor, -1))}
            className="min-h-14 min-w-14 rounded-md border-2 border-slate-400 px-4 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            aria-label="Mes anterior"
          >
            ‹
          </button>
          <h2 className="text-2xl font-semibold">{monthLabel(`${anchor}-01`)}</h2>
          <button
            type="button"
            onClick={() => setAnchor(addMonths(anchor, 1))}
            className="min-h-14 min-w-14 rounded-md border-2 border-slate-400 px-4 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            aria-label="Mes siguiente"
          >
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium text-slate-500">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
            <div key={day} className="py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1">
          {weeks.flat().map((day) => {
            const outside = !isSameMonth(day, anchor)
            const selected = inRange(day, from, to)
            const closed = alreadyClosed.has(day)
            const past = day < today
            return (
              <button
                key={day}
                type="button"
                disabled={closed || past}
                onClick={() => {
                  const next = nextSelection(day, from, to)
                  setFrom(next.from)
                  setTo(next.to)
                  setConfirming(false)
                }}
                className={[
                  // min-h-14 everywhere: these are the squares a 91-year-old
                  // has to hit, and a calendar is the densest thing we ask of him.
                  'min-h-14 rounded-md border-2 text-lg tabular-nums focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                  selected
                    ? 'border-slate-900 bg-slate-900 font-semibold text-white'
                    : closed
                      ? 'cursor-not-allowed border-amber-300 bg-amber-100 text-amber-900'
                      : past
                        ? 'cursor-not-allowed border-slate-200 text-slate-300'
                        : 'border-slate-300 bg-white hover:bg-slate-100',
                  outside && !selected ? 'text-slate-400' : '',
                ].join(' ')}
              >
                {Number(day.slice(8))}
              </button>
            )
          })}
        </div>

        <p className="mt-3 text-base text-slate-600">
          <span className="mr-4 inline-flex items-center gap-2">
            <span className="inline-block size-4 rounded border-2 border-amber-300 bg-amber-100" />
            ya cerrado
          </span>
          {from && (
            <span className="font-medium text-slate-900">
              {to && to !== from
                ? `${formatDayLong(from)} a ${formatDayLong(to)} — ${span} días`
                : `${formatDayLong(from)} — elegir el último día, o confirmar por un solo día`}
            </span>
          )}
        </p>
      </section>

      {from && (
        <section className="rounded-lg border-2 border-slate-300 bg-white p-6">
          <h2 className="text-2xl font-semibold">¿Por qué?</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {REASONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setReason(option.value)}
                className={[
                  'min-h-14 rounded-md border-2 px-6 text-lg focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                  reason === option.value
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-400 bg-white',
                ].join(' ')}
              >
                {option.label}
              </button>
            ))}
          </div>

          <label className="mt-5 block">
            <span className="text-base font-medium text-slate-700">
              ¿Algo para avisar a los clientes?
            </span>
            <span className="ml-2 text-sm text-slate-500">
              Aparece en el correo y en la página de reservas.
            </span>
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Volvemos el lunes"
              className="mt-1 min-h-14 w-full rounded-md border-2 border-slate-400 px-4 text-xl"
            />
          </label>

          {!confirming ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
              >
                Ver a quiénes afecta
              </button>
              <button
                type="button"
                onClick={reset}
                className="min-h-16 rounded-md border-2 border-slate-400 px-8 text-xl"
              >
                Empezar de nuevo
              </button>
            </div>
          ) : (
            <Confirm
              preview={preview}
              busy={close.isPending}
              error={close.error}
              onCancel={() => setConfirming(false)}
              onConfirm={() =>
                close.mutate({
                  data: {
                    starts_on: from,
                    ends_on: to ?? from,
                    reason,
                    note: note.trim() || null,
                  },
                })
              }
            />
          )}
        </section>
      )}

      <ClosureList closures={closures.data?.closures ?? []} onChanged={refresh} />
    </div>
  )
}

type PreviewQuery = {
  isPending: boolean
  isError: boolean
  error: unknown
  data: ClosurePreview | undefined
}

function Confirm({
  preview,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  preview: PreviewQuery
  busy: boolean
  error: unknown
  onCancel: () => void
  onConfirm: () => void
}) {
  if (preview.isPending) return <p className="mt-6 text-slate-600">Verificando…</p>
  if (preview.isError) {
    return (
      <p role="alert" className="mt-6 text-red-700">
        {apiMessage(preview.error, 'No se pudieron verificar esas fechas.')}
      </p>
    )
  }

  if (!preview.data) return null
  const { bookings, customers_to_email: emailing, days } = preview.data

  return (
    <div className="mt-6 rounded-md border-2 border-amber-400 bg-amber-50 p-5">
      <h3 className="text-xl font-semibold">
        {bookings.length === 0
          ? `Cerrando ${days} ${days === 1 ? 'día' : 'días'}. No hay nadie reservado.`
          : `Esto cancela ${bookings.length} ${
              bookings.length === 1 ? 'reserva' : 'reservas'
            } y avisa a ${emailing} ${emailing === 1 ? 'persona' : 'personas'}.`}
      </h3>

      {bookings.length > 0 && (
        <>
          {/* By NAME, not a count. A number is easy to click past; a list of
              people you know is what makes a wrong date obvious. */}
          <ul className="mt-4 max-h-64 space-y-1 overflow-y-auto text-base">
            {bookings.map((booking) => (
              <li key={booking.booking_id} className="flex flex-wrap gap-x-3">
                <span className="font-medium">{booking.customer_name ?? 'Unnamed'}</span>
                <span className="tabular-nums text-slate-700">
                  {formatShortInstant(booking.starts_at)}
                </span>
                {booking.paid && <span className="text-green-800">pago — se devuelve todo</span>}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-base text-slate-700">
            Quien haya pagado recibe todo de vuelta, sin importar la anticipación. Esto no se puede
            deshacer: reabrir los días después no devuelve las reservas.
          </p>
        </>
      )}

      {Boolean(error) && (
        <p role="alert" className="mt-3 text-red-700">
          {apiMessage(error, 'El cambio no se aplicó.')}
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className="min-h-16 rounded-md bg-red-700 px-8 text-xl text-white disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-red-900"
        >
          {busy ? 'Closing…' : 'Sí, cerrar el local'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-16 rounded-md border-2 border-slate-400 bg-white px-8 text-xl"
        >
          No, volver
        </button>
      </div>
    </div>
  )
}

function ClosureList({
  closures,
  onChanged,
}: {
  closures: ClosureRead[]
  onChanged: () => Promise<unknown>
}) {
  const reopen = useReopenFacility({ mutation: { retry: false, onSuccess: onChanged } })
  const today = todayAtFacility()

  if (closures.length === 0) return null

  return (
    <section>
      <h2 className="text-2xl font-semibold">Días cerrados</h2>
      <ul className="mt-4 space-y-3">
        {closures.map((closure) => {
          const over = closure.ends_on < today
          return (
            <li
              key={closure.id}
              className={[
                'flex flex-wrap items-center gap-4 rounded-lg border-2 bg-white px-5 py-4',
                over ? 'border-slate-200 text-slate-500' : 'border-slate-300',
              ].join(' ')}
            >
              <div>
                <p className="text-xl font-medium">
                  {closure.days === 1
                    ? formatDayLong(closure.starts_on)
                    : `${formatDayLong(closure.starts_on)} to ${formatDayLong(closure.ends_on)}`}
                </p>
                <p className="text-base text-slate-600">
                  {REASONS.find((r) => r.value === closure.reason)?.label ?? closure.reason}
                  {closure.note && ` — ${closure.note}`}
                </p>
              </div>
              {!over && (
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm('¿Reabrir estos días? Las reservas canceladas NO vuelven.')
                    ) {
                      reopen.mutate({ closureId: closure.id })
                    }
                  }}
                  className="ml-auto min-h-14 rounded-md border-2 border-slate-400 px-6 text-lg"
                >
                  Reabrir
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
